<template>
  <aside
    class="consult-float-bar"
    :class="{ 'consult-float-bar--embedded': !fixed }"
    :style="embeddedStyle"
  >
    <div class="consult-float-bar__hotline consult-hotline">
      <div class="consult-hotline__head">
        <span class="consult-hotline__icon" aria-hidden="true">
          <slot name="hotline-icon">
            <img
              class="consult-hotline__icon-img"
              :src="defaultHotlineIconSrc"
              alt=""
              aria-hidden="true"
            />
          </slot>
        </span>
        <span class="consult-hotline__title">咨询热线</span>
      </div>
      <div class="consult-hotline__body">
        <span class="consult-hotline__tel">{{ phone }}</span>
      </div>
    </div>

    <div class="consult-float-bar__wechat consult-wechat">
      <p class="consult-wechat__hint">手机微信扫描二维码</p>
      <div class="consult-wechat__qr-wrap">
        <img
          v-if="qrSrc"
          class="consult-wechat__qr"
          :src="qrSrc"
          alt="微信咨询二维码"
        />
        <div v-else class="consult-wechat__qr-placeholder" aria-hidden="true" />
      </div>
      <p class="consult-wechat__hint consult-wechat__hint--pc">
        电脑微信点击咨询
      </p>
      <a
        v-if="consultHref"
        class="consult-wechat__btn"
        :href="consultHref"
        target="_blank"
        rel="noopener noreferrer"
      >
        点击咨询
      </a>
      <button
        v-else
        type="button"
        class="consult-wechat__btn"
        @click="$emit('consult-click')"
      >
        点击咨询
      </button>
    </div>
  </aside>
</template>

<script>
/** 默认二维码（组件同目录 img/wxdd.png） */
const DEFAULT_QR_SRC = require("./img/wxdd.png");
/** 咨询热线标题前默认图标（组件同目录 img/phone.png） */
const DEFAULT_HOTLINE_ICON_SRC = require("./img/phone.png");
/** 默认企业微信客服接待链接 */
const DEFAULT_CONSULT_HREF =
  "https://work.weixin.qq.com/kfid/kfc60a0c7b92c1cb937";

/** 与样式 .consult-float-bar 宽度一致，用于留白区居中计算 */
const BAR_WIDTH_PX = 180;

/**
 * 右侧悬浮：咨询热线 + 微信扫码 / 点击咨询。
 * 其他页面引入后可通过 fixed=false 改为文档流内联展示。
 * 热线标题前图标：使用插槽 hotline-icon 自定义；默认 phone.png。
 */
export default {
  name: "ConsultFloatBar",
  props: {
    /** 展示的热线号码 */
    phone: {
      type: String,
      default: "4006611558",
    },
    /** 二维码图片地址；默认 wxdd.png，传空字符串可仅显示占位 */
    qrSrc: {
      type: String,
      default() {
        return DEFAULT_QR_SRC;
      },
    },
    /** 「点击咨询」外链；默认企业微信客服链接，传空则用按钮并触发 consult-click */
    consultHref: {
      type: String,
      default: DEFAULT_CONSULT_HREF,
    },
    /** 为 true 时 fixed 在视口右侧垂直居中 */
    fixed: {
      type: Boolean,
      default: true,
    },
    /** fixed 且未启用 alignInRightGutter 时，距离视口右侧（与 minRight 无关） */
    right: {
      type: [Number, String],
      default: 24,
    },
    zIndex: {
      type: Number,
      default: 99,
    },
    /**
     * 为 true 时，fixed 定位下将栏置于「主内容区」右侧留白带的水平中央
     * （按 contentMaxWidth 与视口算出主内容右缘，再算留白区中点）。
     */
    alignInRightGutter: {
      type: Boolean,
      default: true,
    },
    /** 与页面主区域 max-width 对齐，个人端列表/帮助页等为 1200 */
    contentMaxWidth: {
      type: Number,
      default: 1200,
    },
    /** 悬浮栏占位宽度（px），须与样式 width 一致 */
    barWidth: {
      type: Number,
      default: BAR_WIDTH_PX,
    },
    /** 留白居中计算结果不小于该值（避免贴边） */
    minRight: {
      type: Number,
      default: 24,
    },
  },
  data() {
    return {
      defaultHotlineIconSrc: DEFAULT_HOTLINE_ICON_SRC,
      viewportWidth:
        typeof window !== "undefined" ? window.innerWidth : 1200,
    };
  },
  computed: {
    fixedRightStyle() {
      if (!this.fixed) return "";
      if (!this.alignInRightGutter) {
        return typeof this.right === "number" ? `${this.right}px` : this.right;
      }
      const W = this.viewportWidth;
      const cw = Math.min(this.contentMaxWidth, W);
      const contentRight = (W + cw) / 2;
      let r = (W - this.barWidth - contentRight) / 2;
      if (r < this.minRight) r = this.minRight;
      return `${r}px`;
    },
    embeddedStyle() {
      if (!this.fixed) return {};
      return {
        right: this.fixedRightStyle,
        zIndex: this.zIndex,
      };
    },
  },
  mounted() {
    if (!this.fixed || !this.alignInRightGutter) return;
    this._syncViewport = () => {
      this.viewportWidth = window.innerWidth;
    };
    window.addEventListener("resize", this._syncViewport);
    this._syncViewport();
  },
  beforeDestroy() {
    if (this._syncViewport) {
      window.removeEventListener("resize", this._syncViewport);
    }
  },
};
</script>

<style scoped>
.consult-float-bar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 180px;
  opacity: 1;
  visibility: visible;
  transition:
    opacity 0.38s ease,
    visibility 0.38s ease,
    transform 0.38s ease;
}

.consult-float-bar:not(.consult-float-bar--embedded) {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
}

/* —— 咨询热线卡片 —— */
.consult-hotline {
  box-shadow: 0px 2px 8px 0px rgba(22, 30, 44, 0.1);
  background: linear-gradient(0deg, #5b85ff 0%, #5ba8ff 100%);
  /* background: #5b85ff; */
  border-radius: 8px;
  overflow: hidden;
  padding: 3px;
}

.consult-hotline__head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
}

.consult-hotline__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.consult-hotline__icon-img {
  display: block;
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.consult-hotline__title {
  line-height: 1;
}

.consult-hotline__body {
  box-sizing: border-box;
  padding: 10px 12px 10px;
  text-align: center;
  background: #fff;
  border-radius: 6px;
  border-top: none;
}

.consult-hotline__tel {
  display: inline-block;
  font-size: 20px;
  font-weight: 700;
  color: #5b85ff;
  letter-spacing: 0.02em;
  line-height: 1.2;
  cursor: default;
  user-select: text;
}

/* —— 微信咨询卡片 —— */
.consult-wechat {
  box-sizing: border-box;
  padding: 20px 20px 20px;
  background: #ffffff;
  box-shadow: 0px 2px 8px 0px rgba(22, 30, 44, 0.1);
  border-radius: 8px;
  text-align: center;
}

.consult-wechat__hint {
  margin: 0;
  font-size: 14px;
  color: #000000;
  line-height: 1.4;
}

.consult-wechat__hint--pc {
  margin-top: 15px;
}

.consult-wechat__qr-wrap {
  margin-top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.consult-wechat__qr {
  display: block;
  width: 120px;
  height: 120px;
  object-fit: contain;
  border-radius: 4px;
  box-sizing: border-box;
}

.consult-wechat__qr-placeholder {
  width: 120px;
  height: 120px;
  border: 1px solid #5b85ff;
  border-radius: 4px;
  box-sizing: border-box;
  background: #f7f9fc;
}

.consult-wechat__btn {
  display: block;
  width: 100%;
  margin-top: 15px;
  padding: 9px 12px;
  border: none;
  border-radius: 6px;
  background: #5b85ff;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  box-sizing: border-box;
  line-height: 1.3;
  font-family: inherit;
}

.consult-wechat__btn:hover {
  opacity: 0.92;
}

/* 视口宽度不足时仅隐藏右侧 fixed 悬浮条，避免遮挡主内容；嵌入式（弹窗内等）始终可见 */
@media (max-width: 1559px) {
  .consult-float-bar:not(.consult-float-bar--embedded) {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(-50%) translateX(20px);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/consultFloatBar/index.vue